<template>
  <v-card flat class="border-0 !bg-[rgb(var(--v-theme-surface))]">
    <!-- SC-45762: header row — title + info tooltip left, + New Deposit button right -->
    <v-card-title class="d-flex align-center justify-space-between py-3 px-4">
      <span class="d-flex align-center gap-1">
        Step 1: Select Settlement Deposits
        <LlTooltip
          icon="info"
          text="Select the trust deposits that make up the settlement recovery. The recovery amount is the sum of all selected deposits."
        />
      </span>
      <LlSecondaryButton
        color="primary"
        size="small"
        @click="$emit('open-trust-deposit')"
      >
        + New Deposit
      </LlSecondaryButton>
    </v-card-title>

    <v-card-text>

        <!-- Description paragraph — old design only -->
        <div v-if="!redesignEnabled" class="text-body-2 text-grey-darken-1 mb-4">
          Select the trust deposits that make up the settlement recovery. The recovery amount is the sum of all selected deposits.
        </div>

        <!-- Deposits-as-of date filter — hidden while blocked; there is no list to filter -->
        <div v-if="!showClientLevelNotice" class="flex items-center gap-3 mb-4" data-cy="deposit-date-range">
          <div style="width: 170px">
            <LlDatePicker
              :model-value="startDateValue"
              label="Deposits as of"
              data-cy="deposit-start-date"
              :show-button="false"
              :hide-details="true"
              @update:model-value="onStartDateChange"
            />
          </div>
        </div>
        <!-- Caption text — old design only -->
        <div v-if="!redesignEnabled" class="text-caption text-grey mb-4">
          Showing trust deposits from <strong>{{ activeRangeLabel }}</strong>.
        </div>

        <!-- Client-level billing notice — pre-V4 only; false once the backend resolves the client ledger -->
        <AlertBar
          v-if="showClientLevelNotice"
          class="mb-4"
          data-cy="client-level-billing-notice"
        >
          Settlement deposits currently only support matter-level billing.
          This matter uses client-level billing, so some deposits may not appear here.
        </AlertBar>

        <!-- The list is withheld while blocked: it is matter-scoped, so it may be partial for
             this client, and confirming freezes the recovery amount everything derives from. -->
        <template v-if="!showClientLevelNotice">

        <!-- Deposit loading spinner -->
        <div v-if="deposits.loading" class="py-4 text-center">
          <v-progress-circular indeterminate color="success" size="28" />
        </div>

        <!-- No deposits empty state -->
        <AlertBar
          v-else-if="deposits.items.length === 0"
          class="mb-4"
        >
          <div class="font-weight-bold mb-1">{{ emptyStateHeading }}</div>
          <div class="text-body-2">Widen the date range above, or use the + New Deposit button to record one.</div>
        </AlertBar>

        <!-- Deposit table -->
        <template v-else>

          <v-table density="compact" class="mb-2">
            <thead>
              <tr>
                <th style="width: 40px">
                  <LlCheckbox
                    :model-value="allDepositsSelected"
                    :disabled="allDepositsLocked"
                    aria-label="Select all deposits"
                    @update:model-value="$emit('toggle-all-deposits')"
                  />
                </th>
                <th class="text-uppercase text-caption">Date</th>
                <th class="text-uppercase text-caption">Payor / Source</th>
                <th class="text-uppercase text-caption">Description</th>
                <th class="text-uppercase text-caption text-right">Amount</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="dep in deposits.items"
                :key="dep.depositId"
                :class="{
                  'bg-green-50': deposits.selectedIds.includes(dep.depositId),
                  'text-disabled': dep.isLocked,
                }"
              >
                <td>
                  <!-- No data-cy: LlTooltip's v-if/v-else roots are a fragment, so it never lands. -->
                  <LlTooltip location="top" :max-width="300" :text="lockedHoverText(dep)">
                    <template #activator>
                      <!-- Vuetify sets pointer-events:none on a disabled control, so a locked
                           checkbox never receives the hover itself — this wrapper has to, and needs
                           a real box to do it, which LlTooltip's bare inline span does not give. -->
                      <span class="d-inline-flex">
                        <LlCheckbox
                          :model-value="deposits.selectedIds.includes(dep.depositId)"
                          :disabled="dep.isLocked"
                          :aria-label="`Select deposit from ${dep.receivedFrom} for ${formatCurrency(dep.amount)}`"
                          class="d-inline-flex"
                          @update:model-value="$emit('toggle-deposit', dep.depositId)"
                        />
                      </span>
                    </template>
                  </LlTooltip>
                </td>
                <td class="text-body-2">{{ getShortDateString(dep.receivedDate) }}</td>
                <td class="text-body-2">{{ dep.receivedFrom }}</td>
                <td class="text-body-2">{{ dep.description }}</td>
                <td class="text-body-2 text-right">{{ formatCurrency(dep.amount) }}</td>
              </tr>
            </tbody>

            <!-- tfoot — old design only; redesign uses external bar below -->
            <tfoot v-if="!redesignEnabled">
              <tr>
                <td colspan="4" class="text-body-1 font-weight-bold py-3">Selected Recovery Amount:</td>
                <td class="text-body-1 font-weight-bold text-right py-3"
                    :class="recoveryAmount > 0 ? 'text-green-darken-2' : ''">
                  {{ formatCurrency(recoveryAmount) }}
                </td>
              </tr>
            </tfoot>
          </v-table>

          <!-- Selected Recovery Amount bar — redesign only (outside table to avoid gap/tfoot padding) -->
          <div v-if="redesignEnabled" class="d-flex justify-space-between align-center px-3 py-3 mb-3" style="border-top: 2px solid #e0e0e0;">
            <span class="text-body-1 font-weight-bold">Selected Recovery Amount:</span>
            <span
              class="font-weight-bold"
              style="font-size: 16px;"
              :class="recoveryAmount > 0 ? 'text-green-darken-2' : 'text-grey-lighten-1'"
            >
              {{ formatCurrency(recoveryAmount) }}
            </span>
          </div>

          <!-- Every deposit claimed elsewhere — "select one" is advice the UI won't let them act on -->
          <div
            v-if="allDepositsLocked"
            class="text-body-2 text-orange-darken-3 mb-3"
            data-cy="all-deposits-locked-hint"
          >
            Every deposit in this date range is already part of a settlement for another matter.
            Widen the date range above, edit that settlement to exclude a deposit, or record a new one.
          </div>

          <!-- No deposits selected hint -->
          <div
            v-else-if="deposits.selectedIds.length === 0"
            class="text-body-2 text-orange-darken-3 mb-3"
            data-cy="no-deposits-selected-hint"
          >
            Select at least one deposit to define the recovery amount
          </div>

          <!-- Contingency Fee % — old design only -->
          <div v-if="!redesignEnabled" class="mb-4">
            <div class="text-overline text-grey-darken-1 mb-1">Contingency Fee %</div>
            <div class="text-h6 font-weight-bold">{{ feePercentage }}%</div>
            <div class="text-caption text-grey">Configured in Settlement Settings above.</div>
          </div>

          <!-- Validation error (EC-9) -->
          <div
            v-if="validationError"
            class="text-body-2 text-error mb-2"
          >
            <LlIcon icon="mdi-alert-circle" size="14" color="error" class="mr-1" />
            {{ validationError }}
          </div>

        </template>

        </template>

        <!-- One footer for both paths. Blocked, it stays in place but disabled and without a
             figure — the amount would come from the list we are withholding. -->
        <div v-if="showConfirmFooter" class="sett-step-footer">
          <LlSecondaryButton
            color="primary"
            style="margin-left: auto"
            :disabled="!canConfirm || showClientLevelNotice"
            :loading="saving"
            @click="$emit('confirm-recovery')"
          >
            {{ confirmLabel }} &rarr;
          </LlSecondaryButton>
        </div>

    </v-card-text>
  </v-card>
</template>

<script setup lang="ts">
import { computed, type PropType } from 'vue';
import LlCheckbox from '@/components/LlCheckbox.vue';
import LlSecondaryButton from '@/components/LlSecondaryButton.vue';
import LlTooltip from '@/components/LlTooltip.vue';
import LlDatePicker from '@/components/LlDatePicker.vue';
import LlIcon from '@/components/LlIcon.vue';
import AlertBar from '@/components/AlertBar.vue';
import { formatCurrency, getShortDateString, yyyymmdddash, parseLocalDate } from '@/services/utils';
import type { DepositItem, DepositsState } from '@/models/settlementTypes';

const props = defineProps({
  deposits: { type: Object as PropType<DepositsState>, required: true },
  allDepositsSelected: { type: Boolean, default: false },
  recoveryAmount: { type: Number, default: 0 },
  feePercentage: { type: Number, default: 0 },
  canConfirm: { type: Boolean, default: false },
  saving: { type: Boolean, default: false },
  validationError: { type: String, default: '' },
  /** Pre-V4 only — drives the matter-level-billing notice. */
  isClientLevelBilling: { type: Boolean, default: false },
  /** `settlementClientAccounting` flipper: on, drops the matter-only copy and the notice. */
  clientAccountingEnabled: { type: Boolean, default: false },
  /** Gate all SC-45762 redesign UI behind this flag.
   *  false = old design (description paragraph, caption, tfoot, fee % section).
   *  true  = new design (tooltip header, total outside table, no contingency %). */
  redesignEnabled: { type: Boolean, default: false },
});

const emit = defineEmits([
  'toggle-all-deposits',
  'toggle-deposit',
  'open-trust-deposit',
  'apply-date-range',
  'confirm-recovery',
]);

// Retired by AC-V4-1 once the flipper is on — the backend resolves the client ledger, so
// there is nothing left to warn about.
const showClientLevelNotice = computed(
  () => !props.clientAccountingEnabled && props.isClientLevelBilling,
);

// Present while blocked so the step keeps its shape, and otherwise only once there is
// something to confirm — an empty or still-loading list has never offered the button.
const showConfirmFooter = computed(
  () =>
    showClientLevelNotice.value ||
    (!props.deposits.loading && props.deposits.items.length > 0),
);

// No figure while blocked — it would be summed from the list we are withholding.
const confirmLabel = computed(() =>
  showClientLevelNotice.value
    ? 'Confirm Recovery'
    : `Confirm Recovery (${formatCurrency(props.recoveryAmount)})`,
);

// Drop the branch, keeping the neutral string, when the flipper goes.
const emptyStateHeading = computed(() =>
  props.clientAccountingEnabled
    ? 'No trust deposits found.'
    : 'No deposits found in trust for this matter.',
);

// The store's active filter is the single source of truth — the picker reads
// from it one-way (no local ref, no sync watcher), so a store reload updates
// the picker automatically without risking a feedback loop.
const startDateValue = computed<Date | null>(() => parseLocalDate(props.deposits.filterStartDate));

// Changing the picker reloads the deposits. The equality guard swallows the
// echo LlDatePicker re-emits when its bound value changes after a store reload,
// preventing a reload loop.
function onStartDateChange(val: Date | null) {
  const next = yyyymmdddash(val) || undefined;
  if (next === (props.deposits.filterStartDate ?? undefined)) return;
  emit('apply-date-range', { startDate: next });
}

const activeRangeLabel = computed(() => {
  const start = props.deposits.filterStartDate;
  return `${start ? getShortDateString(start) : '—'} to today`;
});

const allDepositsLocked = computed(
  () => props.deposits.items.length > 0 && props.deposits.items.every((d) => d.isLocked),
);

/** DD-V4-7 hover. Undefined when the row is selectable — LlTooltip then renders the
 *  bare checkbox with no tooltip wrapper. */
function lockedHoverText(deposit: DepositItem): string | undefined {
  if (!deposit.isLocked) return undefined;
  // trim() not ??: a blank name is not null, and would render "for matter ."
  const matterName = deposit.lockedByMatterName?.trim() || 'another matter';
  return (
    `You cannot select this deposit because it is associated with a settlement for matter ${matterName}. `
    + 'If this is in error, please edit that settlement to exclude this deposit.'
  );
}
</script>
